/** Screen chrome: the phone top bar and menu, the desktop header with navigation, and the footer. */
import { availablePoints } from '@caged/shared';
import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LEAGUE_SCREENS, useApp, type Screen } from '../../state/store';
import { formatNumber } from '../format';
import { levelView } from '../progression';
import { XpBar } from './Bars';
import { colorCss } from '../../assets/look';
import { bodyUrl } from './FighterArt';
import { useManifest, useMyFighter, useMyLook } from '../hooks';
import { LanguageSheet, languageOf } from './LanguageSheet';
import { LeagueNavBadge } from './LeagueNavBadge';
import { Logo } from './Logo';
import { Modal } from './Modal';
import s from './Chrome.module.css';
import ui from './ui.module.css';

/** The player's coins; a tap opens the Locker, where they're spent (M7). */
export function WalletChip({ className }: { className?: string }) {
  const { t } = useTranslation();
  const coins = useApp((st) => st.progress.coins);
  const screen = useApp((st) => st.screen);
  const go = useApp((st) => st.go);
  const content = (
    <>
      <span className={ui.coin} aria-hidden="true" />
      <span aria-hidden="true">{formatNumber(coins)}</span>
    </>
  );
  if (screen === 'locker') {
    return (
      <span className={[s.wallet, className ?? ''].join(' ')} aria-label={t('screens.ui.coinsLabel', { count: coins })}>
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={[s.wallet, s.walletBtn, className ?? ''].join(' ')}
      onClick={() => go('locker')}
      aria-label={`${t('screens.ui.coinsLabel', { count: coins })} · ${t('screens.locker.title')}`}
    >
      {content}
    </button>
  );
}

/** The phone top bar: back button, a mono title, a slot on the right and the menu. */
export function TopBar({
  title,
  onBack,
  right,
  heading = true,
}: {
  title: string;
  onBack?: () => void;
  right?: ReactNode;
  /** The title is the page heading (off when the screen has its own h1). */
  heading?: boolean;
}) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const Title = heading ? 'h1' : 'div';
  return (
    <div className={s.topBar}>
      <button type="button" className={ui.iconBtn} onClick={onBack ?? (() => go('home'))} aria-label={t('common.back')}>
        <span aria-hidden="true">←</span>
      </button>
      <Title className={[ui.label, s.topTitle].join(' ')}>{title}</Title>
      <div className={s.topRight}>
        {right ?? <WalletChip />}
        <MenuButton />
      </div>
    </div>
  );
}

/** The player's fighter in its Locker colour, for the small avatars. */
function useAvatarStyle(fighterId: string) {
  const manifest = useManifest();
  const look = useMyLook();
  const f = manifest?.fighters.find((x) => x.id === fighterId);
  const tint = f ? colorCss(look, f.color) : '';
  return tint ? { filter: tint } : undefined;
}

/** Round avatar button with the player's fighter, used in the phone Home top bar. */
export function AvatarButton() {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const fighterId = useMyFighter();
  const manifest = useManifest();
  const avatar = useAvatarStyle(fighterId);
  return (
    <button type="button" className={[ui.iconBtn, s.avatarBtn].join(' ')} onClick={() => go('profile')} aria-label={t('screens.nav.profile')}>
      <img src={bodyUrl(manifest, fighterId, 'idle')} alt="" className={s.avatarImg} style={avatar} />
    </button>
  );
}

function ProfilePill({ active }: { active: boolean }) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const name = useApp((st) => st.settings.playerName);
  const fighterId = useMyFighter();
  const xpTotal = useApp((st) => st.progress.xpTotal);
  const manifest = useManifest();
  const avatar = useAvatarStyle(fighterId);
  const lv = levelView(xpTotal);
  return (
    <button
      type="button"
      className={[s.pill, active ? s.pillActive : ''].join(' ')}
      onClick={() => go('profile')}
      aria-label={t('screens.nav.profileOf', { name, level: lv.level })}
    >
      <span className={s.pillAvatar} aria-hidden="true">
        <span className={s.pillAvatarBg}>
          <img src={bodyUrl(manifest, fighterId, 'idle')} alt="" style={avatar} />
        </span>
        <span className={s.pillLevel}>{lv.level}</span>
      </span>
      <span className={s.pillText} aria-hidden="true">
        <span className={s.pillName}>{name}</span>
        <XpBar value={lv.frac} height={3} />
      </span>
    </button>
  );
}

const NAV: { screen: Screen; key: string }[] = [
  { screen: 'home', key: 'home' },
  { screen: 'league', key: 'league' },
  { screen: 'training', key: 'training' },
  { screen: 'profile', key: 'profile' },
  { screen: 'locker', key: 'locker' },
  { screen: 'settings', key: 'settings' },
];

/** The navigation entry a screen belongs to: the result is Home's, every league screen League's. */
function navCurrent(screen: Screen): Screen {
  return screen === 'result' ? 'home' : LEAGUE_SCREENS.includes(screen) ? 'league' : screen;
}

/** Skill points to spend, for screen readers next to the Training entry's badge. */
function PointsBadge({ points }: { points: number }) {
  const { t } = useTranslation();
  if (points <= 0) return null;
  return (
    <>
      <span className={ui.badge} aria-hidden="true">
        {points}
      </span>
      <span className={ui.srOnly}>{t('screens.nav.pointsBadge', { count: points })}</span>
    </>
  );
}

/**
 * The phone menu (owner, 2026-10-04): ☰ in the top bar of the menu screens opens the desktop
 * header's navigation as a sheet, with Help & FAQ. A dot on it while skill points wait.
 */
export function MenuButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const points = useApp((st) => availablePoints(st.progress));
  const label = t('screens.nav.menu');
  return (
    <>
      <button
        type="button"
        className={[ui.iconBtn, s.menuBtn].join(' ')}
        onClick={() => setOpen(true)}
        aria-label={points > 0 ? `${label} · ${t('screens.nav.pointsBadge', { count: points })}` : label}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg className={s.menuIcon} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
        {points > 0 && <span className={s.menuDot} aria-hidden="true" />}
      </button>
      {open && <MenuSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function MenuSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const screen = useApp((st) => st.screen);
  const go = useApp((st) => st.go);
  const openLeague = useApp((st) => st.openLeague);
  const points = useApp((st) => availablePoints(st.progress));
  const titleId = useId();
  const currentRef = useRef<HTMLButtonElement>(null);
  const current = navCurrent(screen);
  const items = [...NAV.map((n) => ({ screen: n.screen, label: t(`screens.nav.${n.key}`) })), { screen: 'faq' as Screen, label: t('faq.link') }];
  const pick = (target: Screen) => {
    onClose();
    if (target === screen) return;
    if (target === 'league') openLeague('league');
    else go(target);
  };
  return (
    <Modal onClose={onClose} labelledBy={titleId} initialFocus={currentRef}>
      <div className={s.menuHead}>
        <span id={titleId} className={s.menuTitle}>
          {t('screens.nav.menu')}
        </span>
        <button type="button" className={ui.iconBtn} onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
      </div>
      <nav className={s.menuNav} aria-label={t('screens.nav.label')}>
        <ul className={s.menuList}>
          {items.map((it) => {
            const on = current === it.screen;
            return (
              <li key={it.screen}>
                <button
                  ref={on ? currentRef : undefined}
                  type="button"
                  className={[s.menuItem, on ? s.menuOn : ''].join(' ')}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => pick(it.screen)}
                >
                  <span className={s.menuLabel}>{it.label}</span>
                  <span className={s.menuEnd}>
                    {it.screen === 'league' && <LeagueNavBadge />}
                    {it.screen === 'training' && <PointsBadge points={points} />}
                    <span className={s.menuArrow} aria-hidden="true">
                      →
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </Modal>
  );
}

/** The desktop header (designs/* Desktop): logo, navigation pills, wallet and profile. */
export function DesktopHeader() {
  const { t } = useTranslation();
  const screen = useApp((st) => st.screen);
  const go = useApp((st) => st.go);
  const openLeague = useApp((st) => st.openLeague);
  const points = useApp((st) => availablePoints(st.progress));
  const current = navCurrent(screen);
  return (
    <header className={s.header}>
      <div className={s.headerInner}>
        <button type="button" className={s.logoBtn} onClick={() => go('home')} aria-label={t('screens.nav.home')}>
          <Logo size={32} />
        </button>
        <nav className={s.nav} aria-label={t('screens.nav.label')}>
          <div className={s.navPills}>
            {NAV.map((n) => (
              <button
                key={n.screen}
                type="button"
                className={[s.navItem, current === n.screen ? s.navActive : ''].join(' ')}
                aria-current={current === n.screen ? 'page' : undefined}
                onClick={() => (n.screen === 'league' ? openLeague('league') : go(n.screen))}
              >
                {t(`screens.nav.${n.key}`)}
                {n.screen === 'league' && <LeagueNavBadge />}
                {n.screen === 'training' && <PointsBadge points={points} />}
              </button>
            ))}
          </div>
        </nav>
        <div className={s.headerRight}>
          <WalletChip className={s.walletDesktop} />
          <ProfilePill active={screen === 'profile'} />
        </div>
      </div>
    </header>
  );
}

/** Footer: copyright and the language button. */
export function PageFooter({ wide = false }: { wide?: boolean }) {
  const { t } = useTranslation();
  const language = useApp((st) => st.settings.language);
  const go = useApp((st) => st.go);
  const [open, setOpen] = useState(false);
  const lang = languageOf(language);
  return (
    <footer className={[s.footer, wide ? s.footerWide : ''].join(' ')}>
      <div className={s.footerInner}>
        <span className={s.footerStart}>
          <span className={s.copy}>© 2026 CAGED.GG</span>
          <button type="button" className={s.footLink} onClick={() => go('faq')}>
            {t('faq.link')}
          </button>
        </span>
        <button type="button" className={s.langBtn} onClick={() => setOpen(true)} aria-label={t('screens.language.change', { language: lang.native })}>
          <span lang={lang.code}>{lang.native}</span>
          <span aria-hidden="true">⌄</span>
        </button>
      </div>
      {open && <LanguageSheet onClose={() => setOpen(false)} />}
    </footer>
  );
}
